import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { TourFlag } from '@/components/tour/tours';
import { AUTH_QUERY_KEYS } from '@/constants/query-keys';
import { ROUTES } from '@/constants/routes';
import {
  getTabOrganizationId,
  setCurrentOrganizationId,
  setTabOrganizationId,
} from '@/lib/current-organization';
import { trackSignUp } from '@/lib/analytics';
import { identifyGa4User } from '@/lib/ga4';
import {
  enterIdentity,
  getIdentityEpoch,
  resetIdentityEverywhere,
} from '@/lib/identity';
import { uiLanguageFor } from '@/lib/i18n/language';
import { canWrite, isAgencyStaff } from '@/lib/roles';
import { authApi } from '@/repository/auth';
import { usersApi } from '@/repository/users';
import { BusinessModel, type UiLanguage } from '@/types/generated/enums';
import type {
  ChangePasswordRequest,
  ConfirmForgotPasswordRequest,
  CurrentUserResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  InvitationPrefillResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  ResendVerificationRequest,
  SelfUserUpdate,
  SetPasswordRequest,
  SignupRequest,
  SignupKindsResponse,
  SignupResponse,
  SocialProvidersResponse,
  UserRead,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from '@/types/generated';

// Staff name an organization on every request, so a tab that never switched still names its own.
const pinStaffOrganization = (me: CurrentUserResponse): void => {
  if (!isAgencyStaff(me) || getTabOrganizationId() !== null) return;
  if (me.organization_id != null) setTabOrganizationId(me.organization_id);
};

export const meQueryOptions = queryOptions({
  queryKey: AUTH_QUERY_KEYS.me,
  queryFn: async (): Promise<CurrentUserResponse> => {
    const epochAtRequest = getIdentityEpoch();
    const me = await authApi.me();
    // An identity change while this was in flight makes it the outgoing user.
    if (epochAtRequest === getIdentityEpoch()) {
      setCurrentOrganizationId(me.organization_id ?? null);
      pinStaffOrganization(me);
      identifyGa4User(me);
      // A new account's first loads carry its sign-up id; GTM hears it once.
      if (me.sign_up_event_id) trackSignUp(me.sign_up_event_id);
    }
    return me;
  },
  // Don't retry on 401 — the http interceptor handles the refresh attempt.
  retry: false,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  // A language or org default saved in another tab or device lands when this tab is shown again.
  refetchOnWindowFocus: 'always',
});

// Undefined while `me` is in flight, which reads as "cannot write" until it settles.
export const useCanWrite = (): boolean => {
  const { data: user } = useQuery(meQueryOptions);
  return canWrite(user);
};

// Decides whether a surface is judged on ROAS or on cost per conversion; `me` always precedes it.
export const useBusinessModel = (): BusinessModel => {
  const { data: user } = useQuery(meQueryOptions);
  return user?.organization.business_model ?? BusinessModel.COMMERCE;
};

// The unit an organization states its own amounts in; undefined before `me` lands.
export const useOrganizationCurrency = (): string | undefined => {
  const { data: user } = useQuery(meQueryOptions);
  return user?.organization.currency;
};

// Reads the `me` cache without fetching, so a public page never asks for it and a
// language change that refreshes `me` reaches every consumer.
export const useUiLanguage = (): UiLanguage => {
  const { data: user } = useQuery({ ...meQueryOptions, enabled: false });
  return uiLanguageFor(user);
};

export const socialProvidersQueryOptions = queryOptions({
  queryKey: AUTH_QUERY_KEYS.socialProviders,
  queryFn: (): Promise<SocialProvidersResponse> => authApi.socialProviders(),
  // The launch flag does not move inside a session, and a backend outage must not retry-storm /login.
  retry: false,
  staleTime: Infinity,
});

export const useSocialProviders = (): UseQueryResult<
  SocialProvidersResponse,
  Error
> => useQuery(socialProvidersQueryOptions);

// The agency-layer flag does not move inside a session either.
export const signupKindsQueryOptions = queryOptions({
  queryKey: AUTH_QUERY_KEYS.signupKinds,
  queryFn: (): Promise<SignupKindsResponse> => authApi.signupKinds(),
  retry: false,
  staleTime: Infinity,
});

export const useSignupKinds = (): UseQueryResult<SignupKindsResponse, Error> =>
  useQuery(signupKindsQueryOptions);

export const useLogin = (): UseMutationResult<
  LoginResponse,
  Error,
  LoginRequest
> =>
  useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: (data: LoginRequest): Promise<LoginResponse> =>
      authApi.login(data),
    // A new session may belong to a different tenant than whatever was cached.
    onSuccess: resetIdentityEverywhere,
  });

export const useSignup = (): UseMutationResult<
  SignupResponse,
  Error,
  SignupRequest
> =>
  useMutation<SignupResponse, Error, SignupRequest>({
    mutationFn: (data: SignupRequest): Promise<SignupResponse> =>
      authApi.signup(data),
  });

// Lives beside meQueryOptions because the cache it has to refresh is `me`: the
// sidebar reads the name from there, not from the users list.
export const useUpdateSelf = (): UseMutationResult<
  UserRead,
  Error,
  SelfUserUpdate
> => {
  const queryClient = useQueryClient();
  return useMutation<UserRead, Error, SelfUserUpdate>({
    mutationFn: (data: SelfUserUpdate): Promise<UserRead> =>
      usersApi.updateSelf(data),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
    },
  });
};

// Each tour writes its own flag; `me` is refetched so a reload never shows it again.
export const useCompleteTour = (): UseMutationResult<
  UserRead,
  Error,
  TourFlag
> => {
  const queryClient = useQueryClient();
  return useMutation<UserRead, Error, TourFlag>({
    mutationFn: (flag: TourFlag): Promise<UserRead> =>
      flag === 'portfolio_tour_completed'
        ? usersApi.completePortfolioTour()
        : usersApi.completeOnboarding(),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
    },
  });
};

// The account moves to a different Cognito profile, so `me` is refetched under
// the fresh cookies the response plants.
export const useSetPassword = (): UseMutationResult<
  void,
  Error,
  SetPasswordRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, SetPasswordRequest>({
    mutationFn: (data: SetPasswordRequest): Promise<void> =>
      usersApi.setPassword(data),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
    },
  });
};

// No profile move and no new cookies here, so `me` is unchanged and nothing is refetched.
export const useChangePassword = (): UseMutationResult<
  void,
  Error,
  ChangePasswordRequest
> =>
  useMutation<void, Error, ChangePasswordRequest>({
    mutationFn: (data: ChangePasswordRequest): Promise<void> =>
      usersApi.changePassword(data),
  });

export const useForgotPassword = (): UseMutationResult<
  ForgotPasswordResponse,
  Error,
  ForgotPasswordRequest
> =>
  useMutation<ForgotPasswordResponse, Error, ForgotPasswordRequest>({
    mutationFn: (
      data: ForgotPasswordRequest,
    ): Promise<ForgotPasswordResponse> => authApi.forgotPassword(data),
  });

export const useConfirmForgotPassword = (): UseMutationResult<
  void,
  Error,
  ConfirmForgotPasswordRequest
> =>
  useMutation<void, Error, ConfirmForgotPasswordRequest>({
    mutationFn: (data: ConfirmForgotPasswordRequest): Promise<void> =>
      authApi.confirmForgotPassword(data),
  });

export const useInvitationPrefill = (
  token: string | undefined,
): UseQueryResult<InvitationPrefillResponse, Error> =>
  useQuery({
    queryKey: AUTH_QUERY_KEYS.invitationPrefill(token ?? ''),
    queryFn: (): Promise<InvitationPrefillResponse> =>
      authApi.resolveInvitation(token as string),
    enabled: Boolean(token),
    // A 404 (invalid/expired/consumed) is an expected outcome — don't retry.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

export const useVerifyEmail = (): UseMutationResult<
  VerifyEmailResponse,
  Error,
  VerifyEmailRequest
> =>
  useMutation<VerifyEmailResponse, Error, VerifyEmailRequest>({
    mutationFn: (data: VerifyEmailRequest): Promise<VerifyEmailResponse> =>
      authApi.verifyEmail(data),
    // Verification establishes the session, so it is an identity change too.
    onSuccess: resetIdentityEverywhere,
  });

export const useResendVerification = (): UseMutationResult<
  { message: string },
  Error,
  ResendVerificationRequest
> =>
  useMutation<{ message: string }, Error, ResendVerificationRequest>({
    mutationFn: (
      data: ResendVerificationRequest,
    ): Promise<{ message: string }> => authApi.resendVerification(data),
  });

// Uncomment when the authenticated shell / header is built.
// export const useCurrentUser = (): UseQueryResult<CurrentUserResponse, Error> =>
//   useQuery(meQueryOptions);

export const useLogout = (): UseMutationResult<LogoutResponse, Error, void> =>
  useMutation<LogoutResponse, Error, void>({
    mutationFn: authApi.logout,
    onSettled: (): void => {
      resetIdentityEverywhere();
      enterIdentity(ROUTES.LOGIN);
    },
  });
